import type { DocumentTone } from "@app/components/assistant/conversation/cards/documentTone";
import { documentToneClassNames } from "@app/components/assistant/conversation/cards/documentTone";
import { Button, cn, Expand06, Icon } from "@dust-tt/sparkle";
import type { ComponentProps, ReactNode } from "react";

export type ConversationCardTone = "attention" | "success" | "neutral";

const toneDotClassName: Record<ConversationCardTone, string> = {
  attention: "bg-golden-500",
  success: "bg-success-500",
  neutral: "bg-muted-foreground/50",
};

interface ConversationCardProps {
  visual?: ComponentProps<typeof Icon>["visual"];
  tone?: DocumentTone;
  onOpen?: () => void;
  openLabel?: string;
  eyebrow?: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  status?: { label: string; tone: ConversationCardTone };
  onExpand?: () => void;
  expandLabel?: string;
  headerActions?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  footerStart?: ReactNode;
  className?: string;
  bodyClassName?: string;
}

/**
 * The one frame every in-conversation card shares: provider mark, a title the eye lands on,
 * one line of meta, a compact body, and an action row. Anything bigger than a glance belongs
 * in the 3rd pane, which `onExpand` opens.
 */
export function ConversationCard({
  visual,
  tone,
  onOpen,
  openLabel,
  eyebrow,
  title,
  meta,
  status,
  onExpand,
  expandLabel = "Open in side panel",
  headerActions,
  children,
  footer,
  footerStart,
  className,
  bodyClassName,
}: ConversationCardProps) {
  const toneClassNames = tone ? documentToneClassNames[tone] : null;
  return (
    <div
      className={cn(
        "relative flex w-full max-w-conversation flex-col overflow-hidden rounded-2xl border bg-background shadow-2xs",
        "animate-in fade-in slide-in-from-bottom-1 duration-300",
        toneClassNames?.border ?? "border-border/80",
        onOpen &&
          "transition-colors hover:bg-muted-background/50 has-[>button:focus-visible]:ring-2 has-[>button:focus-visible]:ring-highlight",
        className
      )}
    >
      {onOpen && (
        <button
          type="button"
          aria-label={openLabel}
          onClick={onOpen}
          className="absolute inset-0 z-0 cursor-pointer rounded-2xl outline-none"
        />
      )}
      <div
        className={cn(
          "flex items-start gap-3 px-4 pt-3.5 pb-3",
          onOpen && "pointer-events-none relative"
        )}
      >
        {visual && (
          <div
            className={cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl",
              toneClassNames?.tile ??
                "border border-border/70 bg-muted-background"
            )}
          >
            <Icon visual={visual} size="sm" />
          </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          {(eyebrow || status) && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              {status && (
                <span
                  className={cn(
                    "h-1.5 w-1.5 shrink-0 rounded-full",
                    toneDotClassName[status.tone]
                  )}
                  aria-hidden
                />
              )}
              {status && (
                <span className="font-medium text-foreground/80">
                  {status.label}
                </span>
              )}
              {status && eyebrow && <span aria-hidden>·</span>}
              {eyebrow && <span className="truncate">{eyebrow}</span>}
            </div>
          )}
          <div className="line-clamp-2 text-sm font-semibold leading-snug text-foreground">
            {title}
          </div>
          {meta && (
            <div className="truncate text-xs text-muted-foreground">{meta}</div>
          )}
        </div>
        <div className="pointer-events-auto flex shrink-0 items-center gap-1">
          {headerActions}
          {onExpand && (
            <Button
              variant="ghost"
              size="xs"
              icon={Expand06}
              tooltip={expandLabel}
              aria-label={expandLabel}
              onClick={onExpand}
            />
          )}
        </div>
      </div>
      {children && (
        <div className={cn("relative px-4 pb-3.5", bodyClassName)}>
          {children}
        </div>
      )}
      {(footer || footerStart) && (
        <div className="relative flex flex-wrap items-center justify-between gap-2 border-t border-border/70 bg-muted-background/60 px-4 py-2.5">
          <div className="flex min-w-0 items-center gap-3">{footerStart}</div>
          <div className="flex items-center gap-2">{footer}</div>
        </div>
      )}
    </div>
  );
}
